/**
 * Progressive enhancement for the PDP description.
 * Finds known section headings inside the product description RTE
 * (written by the merchant, unchanged) and restyles the plain <ul> that
 * follows into a scannable spec table / compatibility chips / "Ideal para"
 * card grid. If a heading isn't found, the description renders exactly
 * as before — no content is added or altered, only presentation.
 */

const SPEC_ALIASES = ['especificaciones tecnicas', 'especificaciones', 'datos tecnicos', 'ficha tecnica'];
const COMPAT_ALIASES = ['compatibilidad'];
const IDEAL_ALIASES = ['aplicaciones recomendadas', 'ideal para', 'usos recomendados'];

const ICONS = {
  check:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>',
  zap:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>',
  wifi:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><circle cx="12" cy="20" r="1.4" fill="currentColor" stroke="none"/></svg>',
  mic:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/></svg>',
  smartphone:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="2" width="12" height="20" rx="2"/><line x1="11" y1="18" x2="13" y2="18"/></svg>',
  light:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/><path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14"/></svg>',
  fan:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 12c0-3 1.6-6 4-6 2 0 3 2 1.5 5S13 12 12 12z"/><path d="M12 12c3 0 6 1.6 6 4 0 2-2 3-5 1.5S11 13 12 12z"/><path d="M12 12c0 3-1.6 6-4 6-2 0-3-2-1.5-5S11 12 12 12z"/><path d="M12 12c-3 0-6-1.6-6-4 0-2 2-3 5-1.5S13 11 12 12z"/><circle cx="12" cy="12" r="1.3" fill="currentColor" stroke="none"/></svg>',
  pump:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>',
  gate:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="2" width="7" height="20"/><rect x="14" y="2" width="7" height="20"/><line x1="10" y1="12" x2="14" y2="12"/></svg>',
  briefcase:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
  store:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>',
  home:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
  cpu:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="6" y="6" width="12" height="12" rx="1"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>',
};

/**
 * @param {string} str
 * @returns {string}
 */
function normalize(str) {
  return str
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * @param {string} str
 * @returns {string}
 */
function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * @param {string} key
 * @param {string[]} aliases
 * @returns {boolean}
 */
function matchAlias(key, aliases) {
  return aliases.some((alias) => key.includes(alias));
}

/**
 * Moves `h2` plus its following siblings (up to and including the next
 * <ul>, or the next <h2>, whichever comes first) into a new wrapper div.
 * @param {HTMLHeadingElement} h2
 * @param {string} modifier
 * @returns {HTMLDivElement}
 */
function collectBlock(h2, modifier) {
  const wrapper = document.createElement('div');
  wrapper.className = `pdp-block pdp-block--${modifier} pdp-reveal`;
  /** @type {Node} */ (h2.parentNode).insertBefore(wrapper, h2);
  wrapper.appendChild(h2);

  let node = wrapper.nextSibling;
  let sawList = false;
  while (node && !sawList) {
    const current = node;
    node = node.nextSibling;
    const isElement = current.nodeType === Node.ELEMENT_NODE;
    if (isElement && /** @type {Element} */ (current).tagName === 'H2') break;
    wrapper.appendChild(current);
    if (isElement && /** @type {Element} */ (current).tagName === 'UL') sawList = true;
  }
  return wrapper;
}

/**
 * @param {Element} block
 */
function enhanceSpecs(block) {
  const list = block.querySelector('ul');
  if (!list) return;
  const items = Array.from(list.querySelectorAll(':scope > li'));
  /** @type {{label: string, value: string}[]} */
  const rows = [];
  /** @type {string[]} */
  const features = [];

  items.forEach((li) => {
    const text = (li.textContent || '').trim();
    const idx = text.indexOf(':');
    if (idx > 0 && idx < text.length - 1) {
      rows.push({ label: text.slice(0, idx).trim(), value: text.slice(idx + 1).trim() });
    } else if (text) {
      features.push(text.replace(/\.$/, ''));
    }
  });

  if (!rows.length && !features.length) return;

  const card = document.createElement('div');
  card.className = 'pdp-specs';

  if (rows.length) {
    const table = document.createElement('table');
    table.className = 'pdp-specs__table';
    const tbody = document.createElement('tbody');
    rows.forEach(({ label, value }) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `<td class="pdp-specs__label">${escapeHtml(label)}</td><td class="pdp-specs__value">${escapeHtml(value)}</td>`;
      tbody.appendChild(tr);
    });
    table.appendChild(tbody);
    card.appendChild(table);
  }

  if (features.length) {
    const featureList = document.createElement('ul');
    featureList.className = 'pdp-specs__features';
    features.forEach((feature) => {
      const li = document.createElement('li');
      li.className = 'pdp-specs__feature';
      li.innerHTML = `<span class="pdp-specs__feature-icon">${ICONS.check}</span><span>${escapeHtml(feature)}</span>`;
      featureList.appendChild(li);
    });
    card.appendChild(featureList);
  }

  list.replaceWith(card);
}

/**
 * @param {string} text
 * @returns {string}
 */
function matchCompatIcon(text) {
  const key = normalize(text);
  if (key.includes('alexa') || key.includes('google assistant')) return ICONS.mic;
  if (key.includes('wifi') || key.includes('wi-fi')) return ICONS.wifi;
  if (key.includes('android') || key.includes('ios') || key.includes('iphone') || key.includes('app')) {
    return ICONS.smartphone;
  }
  return ICONS.check;
}

/**
 * @param {Element} block
 */
function enhanceCompat(block) {
  const list = block.querySelector('ul');
  if (!list) return;
  const items = Array.from(list.querySelectorAll(':scope > li'));
  if (!items.length) return;

  list.classList.add('pdp-compat__list');
  items.forEach((li) => {
    const text = (li.textContent || '').trim();
    if (!text) return;
    li.classList.add('pdp-compat__chip');
    li.innerHTML = `<span class="pdp-compat__chip-icon">${matchCompatIcon(text)}</span><span class="pdp-compat__chip-label">${escapeHtml(text)}</span>`;
  });
}

/**
 * @param {string} text
 * @returns {string}
 */
function matchIdealIcon(text) {
  const key = normalize(text);
  if (/lampara|luz|iluminacion|foco/.test(key)) return ICONS.light;
  if (/ventilador/.test(key)) return ICONS.fan;
  if (/bomba|riego/.test(key)) return ICONS.pump;
  if (/porton|garage|cochera/.test(key)) return ICONS.gate;
  if (/oficina/.test(key)) return ICONS.briefcase;
  if (/comercio|negocio|local/.test(key)) return ICONS.store;
  if (/hogar|casa|vivienda|departamento|domicilio|residencial/.test(key)) return ICONS.home;
  if (/router|wifi/.test(key)) return ICONS.wifi;
  if (/televisor|tv\b|audio|computadora|cargador|electrodomestico|aire.*acondicionad|cafetera/.test(key)) {
    return ICONS.cpu;
  }
  return ICONS.zap;
}

/**
 * @param {Element} block
 */
function enhanceIdealFor(block) {
  const heading = block.querySelector('h2');
  const list = block.querySelector('ul');
  if (!list) return;
  const items = Array.from(list.querySelectorAll(':scope > li'));
  if (!items.length) return;

  if (heading) heading.textContent = 'Ideal para';
  list.classList.add('pdp-idealfor__list');
  items.forEach((li, index) => {
    const text = (li.textContent || '').trim().replace(/\.$/, '');
    if (!text) return;
    li.classList.add('pdp-idealfor__card');
    /** @type {HTMLElement} */ (li).style.transitionDelay = `${Math.min(index, 7) * 40}ms`;
    li.innerHTML = `<span class="pdp-idealfor__card-icon">${matchIdealIcon(text)}</span><span class="pdp-idealfor__card-label">${escapeHtml(text)}</span>`;
  });
}

/**
 * Lightweight, self-contained fade-in-on-scroll for the elements this
 * module creates. Kept local (rather than reusing the global `.v-reveal`
 * system) because those elements don't exist yet when that observer
 * does its first pass.
 * @param {NodeListOf<Element>} elements
 */
function observeReveal(elements) {
  if (!elements.length) return;
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );
  elements.forEach((el) => observer.observe(el));
}

function run() {
  const scopes = Array.from(document.querySelectorAll('.product-details .rte')).filter(
    (el) => el.querySelector('h2')
  );
  if (!scopes.length) return;

  scopes.forEach((scope) => {
    Array.from(scope.querySelectorAll('h2')).forEach((h2) => {
      const key = normalize(h2.textContent || '');
      if (matchAlias(key, SPEC_ALIASES)) {
        collectBlock(/** @type {HTMLHeadingElement} */ (h2), 'specs');
      } else if (matchAlias(key, COMPAT_ALIASES)) {
        collectBlock(/** @type {HTMLHeadingElement} */ (h2), 'compat');
      } else if (matchAlias(key, IDEAL_ALIASES)) {
        collectBlock(/** @type {HTMLHeadingElement} */ (h2), 'ideal');
      }
    });

    scope.querySelectorAll('.pdp-block--specs').forEach(enhanceSpecs);
    scope.querySelectorAll('.pdp-block--compat').forEach(enhanceCompat);
    scope.querySelectorAll('.pdp-block--ideal').forEach(enhanceIdealFor);

    observeReveal(scope.querySelectorAll('.pdp-reveal'));
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', run);
} else {
  run();
}
